import { useEffect, useState } from "react";
import { Save, Settings as Cog, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const SettingsManager = ({ tAdmin }) => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    monthly_goal_amount: 0,
    monthly_goal_currency: "EUR",
    admin_large_donation_threshold: 100,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setForm({
        monthly_goal_amount: data.monthly_goal_amount,
        monthly_goal_currency: data.monthly_goal_currency,
        admin_large_donation_threshold: data.admin_large_donation_threshold,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        monthly_goal_amount: Number(form.monthly_goal_amount),
        monthly_goal_currency: form.monthly_goal_currency,
        admin_large_donation_threshold: Number(form.admin_large_donation_threshold),
      });
      setSettings(data);
      toast.success(tAdmin.settings.saved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-2xl p-8 text-center text-[#64748B]">...</div>;
  }

  return (
    <div className="max-w-2xl" data-testid="settings-panel">
      <div className="bg-white rounded-2xl border border-black/5 p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#FDF8EE] flex items-center justify-center">
            <Cog size={18} className="text-[#C5A059]" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-[#0A192F] font-semibold">
              {tAdmin.settings.title}
            </h3>
            <p className="text-sm text-[#64748B]">{tAdmin.settings.subtitle}</p>
          </div>
        </div>

        <div className="space-y-5">
          <Field label={tAdmin.settings.goalAmount}>
            <input
              type="number"
              min="0"
              step="100"
              value={form.monthly_goal_amount}
              onChange={(e) => setForm({ ...form, monthly_goal_amount: e.target.value })}
              className="form-input-md"
              data-testid="settings-goal-amount"
            />
          </Field>

          <Field label={tAdmin.settings.goalCurrency}>
            <select
              value={form.monthly_goal_currency}
              onChange={(e) => setForm({ ...form, monthly_goal_currency: e.target.value })}
              className="form-input-md"
              data-testid="settings-currency"
            >
              <option value="EUR">EUR — €</option>
              <option value="USD">USD — $</option>
              <option value="GBP">GBP — £</option>
            </select>
          </Field>

          <Field label={tAdmin.settings.threshold}>
            <input
              type="number"
              min="0"
              step="10"
              value={form.admin_large_donation_threshold}
              onChange={(e) =>
                setForm({ ...form, admin_large_donation_threshold: e.target.value })
              }
              className="form-input-md"
              data-testid="settings-threshold"
            />
            <p className="text-xs text-[#64748B] mt-1.5">{tAdmin.settings.thresholdHelp}</p>
          </Field>
        </div>

        <div className="flex gap-3 mt-8 pt-6 border-t border-black/5">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#C5A059] hover:bg-[#B38F48] text-white font-medium disabled:opacity-50"
            data-testid="settings-save"
          >
            <Save size={14} /> {saving ? "..." : tAdmin.settings.save}
          </button>
          <button
            onClick={fetchSettings}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#E5E7EB] text-[#64748B] hover:bg-black/5"
            data-testid="settings-refresh"
          >
            <RefreshCw size={14} /> {tAdmin.settings.reset}
          </button>
        </div>
      </div>

      <style>{`
        .form-input-md {
          width: 100%;
          margin-top: 0.4rem;
          padding: 0.7rem 1rem;
          border-radius: 0.5rem;
          border: 1px solid #E5E7EB;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          font-size: 0.95rem;
          color: #0A192F;
          background: #FDFBF7;
        }
        .form-input-md:focus {
          border-color: #002B49;
          box-shadow: 0 0 0 3px rgba(0, 43, 73, 0.1);
          background: #FFF;
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-[#64748B] uppercase tracking-wider">{label}</span>
    {children}
  </label>
);

export default SettingsManager;
